import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import Home from "./pages/Home";
import MainHub from "./pages/MainHub";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import Preisliste from "./pages/Preisliste";
import Eingangsliste from "./pages/Eingangsliste";
import Geschenke from "./pages/Geschenke";
import Werbeartikel from "./pages/Werbeartikel";
import Vorfuehrartikel from "./pages/Vorfuehrartikel";
import Eigenverbrauch from "./pages/Eigenverbrauch";
import Uebersicht from "./pages/Uebersicht";
import Quartalsuebersicht from "./pages/Quartalsuebersicht";
import Rechnungen from "./pages/Rechnungen";
import Analyse from "./pages/Analyse";
import Kunden from "./pages/Kunden";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SupportPage from "./pages/SupportPage";
import DonationThankYou from "./pages/DonationThankYou";
import ZielplanungsKalender from "./pages/ZielplanungsKalender";
import AdminOverview from "./pages/AdminOverview";
import { Toaster } from "./components/ui/sonner";
import { getToken } from "./utils/auth";
import ScrollToTop from "./components/ScrollToTop";
import Footer from "./components/Footer";
import PwaIntroSplash from "./components/PwaIntroSplash";
import ThemeMusicPlayer from "./components/ThemeMusicPlayer";
import { trackUserActivity, initializeNotifications } from "./lib/notificationService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ─── Mitternacht-Bereinigung: Erledigte Todos aus localStorage löschen ───────
import { userStorage } from "./utils/userStorage";

const CLEANUP_DATE_KEY = 'burningteam_last_cleanup';
const TODO_STORAGE_KEYS = {
  dailyTodos:   'zielplanung_daily_todos',
  weeklyTodos:  'zielplanung_weekly_todos',
  monthlyTodos: 'zielplanung_monthly_todos',
  backlogTodos: 'zielplanung_backlog_todos',
  yearPlanning: 'zielplanung_year_planning',
  appointments: 'zielplanung_appointments',
  tasks:        'zielplanung_tasks',
};

function cleanupDoneTodosFromStorage() {
  try {
    // Hilfsfunktion: Objekt mit Array-Werten bereinigen (dailyTodos, weeklyTodos, monthlyTodos)
    const cleanNested = (key) => {
      try {
        const data = JSON.parse(userStorage.getItem(key) || '{}');
        let changed = false;
        Object.keys(data).forEach(k => {
          if (Array.isArray(data[k])) {
            const before = data[k].length;
            data[k] = data[k].filter(t => !t.done);
            if (data[k].length !== before) changed = true;
          }
        });
        if (changed) userStorage.setItem(key, JSON.stringify(data));
      } catch (e) {}
    };

    // Hilfsfunktion: Array bereinigen (backlogTodos, appointments, tasks)
    const cleanArray = (key) => {
      try {
        const data = JSON.parse(userStorage.getItem(key) || '[]');
        if (Array.isArray(data)) {
          const filtered = data.filter(t => !t.done);
          if (filtered.length !== data.length) userStorage.setItem(key, JSON.stringify(filtered));
        }
      } catch (e) {}
    };

    // yearPlanning: 2-Ebenen-Objekt { "2024": { "1": [todos...] } }
    const cleanYearPlanning = () => {
      try {
        const data = JSON.parse(userStorage.getItem(TODO_STORAGE_KEYS.yearPlanning) || '{}');
        let changed = false;
        Object.keys(data).forEach(year => {
          if (data[year] && typeof data[year] === 'object') {
            Object.keys(data[year]).forEach(month => {
              if (Array.isArray(data[year][month])) {
                const before = data[year][month].length;
                data[year][month] = data[year][month].filter(t => !t.done);
                if (data[year][month].length !== before) changed = true;
              }
            });
          }
        });
        if (changed) userStorage.setItem(TODO_STORAGE_KEYS.yearPlanning, JSON.stringify(data));
      } catch (e) {}
    };

    cleanNested(TODO_STORAGE_KEYS.dailyTodos);
    cleanNested(TODO_STORAGE_KEYS.weeklyTodos);
    cleanNested(TODO_STORAGE_KEYS.monthlyTodos);
    cleanArray(TODO_STORAGE_KEYS.backlogTodos);
    cleanArray(TODO_STORAGE_KEYS.appointments);
    cleanArray(TODO_STORAGE_KEYS.tasks);
    cleanYearPlanning();

    // Datum merken + andere Komponenten informieren
    localStorage.setItem(CLEANUP_DATE_KEY, new Date().toISOString().split('T')[0]);
    window.dispatchEvent(new CustomEvent('burningteam-todos-sync'));
  } catch (e) {}
}
// ─────────────────────────────────────────────────────────────────────────────

// Setup axios interceptor to automatically add JWT token to all requests
axios.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Setup axios response interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Layout wrapper to conditionally show footer
function AppLayout() {
  const location = useLocation();
  const hideFooter = ['/', '/login', '/register', '/verify-email', '/forgot-password', '/reset-password'].includes(location.pathname);

  return (
    <div className="App flex flex-col min-h-screen">
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/donation/thank-you" element={<DonationThankYou />} />
          <Route path="/main-hub" element={<MainHub />} />
          <Route path="/kunden" element={<Kunden />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin" element={<AdminOverview />} />
          <Route path="/preisliste" element={<Preisliste />} />
          <Route path="/eingangsliste" element={<Eingangsliste />} />
          <Route path="/geschenke" element={<Geschenke />} />
          <Route path="/werbeartikel" element={<Werbeartikel />} />
          <Route path="/vorfuehrartikel" element={<Vorfuehrartikel />} />
          <Route path="/eigenverbrauch" element={<Eigenverbrauch />} />
          <Route path="/uebersicht" element={<Uebersicht />} />
          <Route path="/quartalsuebersicht" element={<Quartalsuebersicht />} />
          <Route path="/rechnungen" element={<Rechnungen />} />
          <Route path="/analyse" element={<Analyse />} />
          <Route path="/zielplanung" element={<ZielplanungsKalender />} />
          <Route path="/zielplanung-kalender" element={<ZielplanungsKalender />} />
        </Routes>
      </div>
      {!hideFooter && <Footer />}
      <ThemeMusicPlayer />
    </div>
  );
}

function App() {
  // Track user activity for streak calculation when app loads
  useEffect(() => {
    const token = getToken();
    if (token) {
      // Track activity and initialize notifications
      trackUserActivity();
      initializeNotifications();
    }
  }, []);

  // Mitternacht-Bereinigung: Erledigte Todos löschen
  useEffect(() => {
    // Beim Start prüfen: Falls heute noch kein Cleanup → sofort bereinigen
    const lastCleanup = localStorage.getItem(CLEANUP_DATE_KEY);
    const today = new Date().toISOString().split('T')[0];
    if (lastCleanup !== today) {
      cleanupDoneTodosFromStorage();
    }

    // Timer für nächste Mitternacht setzen
    const scheduleMidnight = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setDate(midnight.getDate() + 1);
      midnight.setHours(0, 0, 0, 0);
      const ms = midnight - now;
      return setTimeout(() => {
        cleanupDoneTodosFromStorage();
        scheduleMidnight(); // für nächste Mitternacht neu planen
      }, ms);
    };

    const timer = scheduleMidnight();
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <PwaIntroSplash />
      <BrowserRouter>
        <ScrollToTop />
        <AppLayout />
      </BrowserRouter>
      <Toaster position="top-right" />
    </>
  );
}

export default App;